import PageHeader from "@/components/PageHeader";
import {
  HelpCircle,
  Plug,
  ClipboardList,
  Truck,
  Undo2,
  Headphones,
} from "lucide-react";
import { Link } from "react-router-dom";

const topics = [
  { icon: HelpCircle, t: "Product Questions", d: "General inquiries about specifications, condition, or listings." },
  { icon: Plug, t: "Compatibility Assistance", d: "Help confirming compatibility with your existing setup." },
  { icon: ClipboardList, t: "Order Support", d: "Order status, updates, and post-purchase questions." },
  { icon: Truck, t: "Shipping Information", d: "Carrier details, tracking, and shipping timelines." },
  { icon: Undo2, t: "Returns Assistance", d: "Guidance on the returns process and required information." },
  { icon: Headphones, t: "General Customer Service", d: "Any other support topic — we're happy to help." },
];

const faqs = [
  {
    q: "Do products come with a warranty?",
    a: "Warranty coverage varies by product type and condition grade. Customers should review the product-specific warranty information available on each listing before purchasing.",
  },
  {
    q: "How do I start a return?",
    a: "If a product arrives damaged or significantly different from its description, please contact our customer support team. We will work toward a fair resolution based on the listing and product condition.",
  },
  {
    q: "What if my product is missing accessories?",
    a: "Each listing includes details about what is included. For Open Box, Refurbished, and Used grades, contents may vary. Please review the listing carefully or reach out before purchasing.",
  },
  {
    q: "How long does support take to respond?",
    a: "We respond to inquiries during our business hours, Monday – Friday, 9:00 AM – 5:00 PM PT. Most inquiries receive an initial reply within one business day.",
  },
  {
    q: "Are products tested before sale?",
    a: "Applicable products undergo functional testing as part of our refurbishment process. New, sealed items are sold in their original condition without additional testing.",
  },
];

export default function Returns() {
  return (
    <div data-testid="returns-page">
      <PageHeader
        eyebrow="Returns & Support"
        breadcrumb="Returns & Support"
        title="Customer satisfaction matters."
        subtitle="We strive to provide accurate product descriptions and transparent condition grading. If something isn't right, we'll work toward a fair resolution."
      />

      <section className="bg-white">
        <div className="cnl-container py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            {/* Intro text */}
            <div className="lg:col-span-7 space-y-5 text-[15.5px] leading-relaxed text-gray-700">
              <p>
                We strive to provide accurate product descriptions and
                transparent condition grading for every item we list.
              </p>
              <p>
                Customers should review product-specific warranty and return
                information before purchasing. Coverage varies by product type
                and condition grade.
              </p>
              <p>
                If a product arrives damaged or significantly different from
                its description, our team will work toward a fair resolution.
                Please reach out promptly with your order details and a clear
                description of the issue.
              </p>
            </div>

            {/* Side card */}
            <aside className="lg:col-span-5">
              <div className="border border-gray-200 rounded-sm p-7 bg-[#F9FAFB]">
                <h3 className="text-[18px] font-bold tracking-tight text-[#0B192C]">
                  Need immediate help?
                </h3>
                <p className="mt-2 text-[14px] text-gray-600">
                  Send us a message with your order number and a brief
                  description of your issue.
                </p>
                <Link
                  to="/contact"
                  data-testid="returns-contact-cta"
                  className="mt-5 inline-flex items-center justify-center rounded-sm bg-[#0B192C] px-6 py-3.5 text-[12.5px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#1E3E62]"
                >
                  Contact Support
                </Link>
                <div className="mt-6 border-t border-gray-200 pt-5 space-y-2 text-[13px] text-gray-600">
                  <div>
                    <span className="font-semibold text-[#0B192C]">Hours:</span>{" "}
                    Mon – Fri · 9:00 AM – 5:00 PM PT
                  </div>
                  <div>
                    <span className="font-semibold text-[#0B192C]">Email:</span>{" "}
                    cnltech@proton.me
                  </div>
                  <div>
                    <span className="font-semibold text-[#0B192C]">Phone:</span>{" "}
                    +1 (951) 479-2470
                  </div>
                </div>
              </div>
            </aside>
          </div>

          {/* Support Topics */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold tracking-tight text-[#0B192C] mb-8">
              Support Topics
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {topics.map((t) => (
                <div
                  key={t.t}
                  className="border border-gray-200 rounded-sm p-6 hover:border-[#0B192C] transition-colors bg-white"
                  data-testid={`support-topic-${t.t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-[#0B192C] text-white">
                    <t.icon size={18} />
                  </div>
                  <h3 className="mt-4 text-[16px] font-semibold text-[#0B192C]">
                    {t.t}
                  </h3>
                  <p className="mt-2 text-[13.5px] text-gray-600 leading-relaxed">
                    {t.d}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold tracking-tight text-[#0B192C] mb-8">
              Frequently Asked Questions
            </h2>
            <div className="divide-y divide-gray-200 border border-gray-200 rounded-sm">
              {faqs.map((f, i) => (
                <details
                  key={f.q}
                  className="group px-6 py-5 [&[open]]:bg-gray-50"
                  data-testid={`faq-${i + 1}`}
                >
                  <summary className="flex items-center justify-between cursor-pointer list-none">
                    <span className="text-[15.5px] font-semibold text-[#0B192C] pr-6">
                      {f.q}
                    </span>
                    <span className="text-[#2563EB] text-[20px] leading-none transition-transform group-open:rotate-45 select-none">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-[14px] text-gray-700 leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
